"use client";

import { PropertyPickerCalendar, PropertyPicker as PropertyPickerPrimitive } from "@repo/ui/components/property-picker";
import { CalendarDays } from "lucide-react";
import { useFieldContext } from "@/shared/forms/form-context";

/** Parse a FHIR `date` ("YYYY-MM-DD") as a local calendar date (no TZ shift). */
function parseLocalDate(value: string | undefined): Date | undefined {
  if (!value) return undefined;
  const [y, m, d] = value.split("-").map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d);
}

/** Format a local date back to a FHIR `date` string. */
function formatLocalDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export interface DatePickerFieldProps {
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

/**
 * Calendar date field backed by a `YYYY-MM-DD` string (the FHIR `date` shape),
 * so it slots into string-valued form state. Same picker primitive as
 * `PropertyPickerCalendarField`, which is `Date`-backed.
 */
export function DatePickerField({ placeholder = "Select date", disabled, className }: DatePickerFieldProps) {
  const field = useFieldContext<string>();
  const value = parseLocalDate(field.state.value);
  const displayValue = value
    ? value.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
    : null;

  const commit = (date: Date | null | undefined) => {
    field.handleChange(date ? formatLocalDate(date) : "");
    field.handleBlur();
  };

  return (
    <PropertyPickerPrimitive<Date>
      value={value ?? null}
      onChange={commit}
      disabled={disabled}
      placeholder={placeholder}
      className={className}
      icon={<CalendarDays className="size-3.5" />}
      displayValue={displayValue}
    >
      <PropertyPickerCalendar selected={value} onSelect={(date) => date && commit(date)} mode="single" />
    </PropertyPickerPrimitive>
  );
}
